import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Pagination,
  Paper,
  useMediaQuery,
  useTheme,
  Chip,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Custom hook
import useProductCatalog from "../hooks/useProductCatalog";

// Components
import ProductGrid from "../components/product/ProductGrid";
import ProductSearch from "../components/product/ProductSearch";
import ProductFilters from "../components/product/ProductFilters";
import MobileFilterDrawer from "../components/product/MobileFilterDrawer";

/**
 * Product Catalog Page component
 * Displays a list of products with filtering and search capabilities
 * @returns {JSX.Element} The ProductCatalogPage component
 */
const ProductCatalogPage = () => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    // State
    products,
    loading,
    totalProducts,
    totalPages,
    categories,
    loadingCategories,
    categoryHierarchy,
    searchTerm,
    selectedCategory,
    selectedSubcategory,
    sortBy,
    page,
    priceRange,
    minMaxPrice,
    weightRange,
    minMaxWeight,
    attributeFilters,
    availableAttributes,
    loadingAttributes,
    filterDrawerOpen,
    userSetPriceRange,
    userSetWeightRange,

    // Handlers
    handleSearchChange,
    handleCategoryChange,
    handleSubcategoryChange,
    handleSortChange,
    handlePageChange,
    handlePriceRangeChange,
    handleWeightRangeChange,
    handleAttributeFilterChange,
    clearFilters,
    toggleFilterDrawer,
    setUserSetPriceRange,
    setPriceRange,
    setUserSetWeightRange,
    setWeightRange,
  } = useProductCatalog();

  // Log state for debugging
  useEffect(() => {
    console.log("ProductCatalogPage state:", {
      products: products.length,
      loading,
      categoryHierarchy,
      selectedCategory,
      selectedSubcategory,
      priceRange,
      attributeFilters,
      availableAttributes: availableAttributes.length,
    });
  }, [
    products,
    loading,
    categoryHierarchy,
    selectedCategory,
    selectedSubcategory,
    priceRange,
    attributeFilters,
    availableAttributes,
  ]);

  // Props for ProductFilters component
  const filterProps = {
    categoryHierarchy,
    categories,
    selectedCategory,
    selectedSubcategory,
    handleCategoryChange,
    handleSubcategoryChange,
    priceRange,
    minMaxPrice,
    handlePriceRangeChange,
    weightRange,
    minMaxWeight,
    handleWeightRangeChange,
    setUserSetWeightRange,
    setWeightRange,
    availableAttributes,
    attributeFilters,
    handleAttributeFilterChange,
    clearFilters,
    loadingAttributes,
    setUserSetPriceRange,
    setPriceRange,
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          fontFamily="'Playfair Display', serif"
          fontWeight={600}
          color="text.primary"
        >
          {tProducts("catalog.title")}
        </Typography>
        <Divider sx={{ mb: 3, borderColor: "primary.main" }} />
      </Box>

      {/* Search and Sort Controls */}
      <ProductSearch
        searchTerm={searchTerm}
        handleSearchChange={handleSearchChange}
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        toggleFilterDrawer={toggleFilterDrawer}
      />

      <Grid container spacing={3}>
        {/* Filters - Desktop */}
        {!isMobile && (
          <Grid item xs={12} md={3} lg={2.5}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "8px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                border: "1px solid",
                borderColor: "primary.light",
              }}
            >
              <ProductFilters {...filterProps} />
            </Paper>
          </Grid>
        )}

        {/* Products */}
        <Grid item xs={12} md={9} lg={9.5}>
          {/* Results Count and Active Filters */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading
                ? t("common.loading")
                : products.length > 0
                ? tProducts("catalog.search.showingCount", {
                    count: products.length,
                  })
                : tProducts("catalog.search.noResults")}
            </Typography>

            {/* Active Filters */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedCategory &&
                categories.find((c) => c.id === selectedCategory) && (
                  <Chip
                    label={`${tProducts("catalog.filters.categories")}: ${
                      categories.find((c) => c.id === selectedCategory).name
                    }`}
                    onDelete={() =>
                      handleCategoryChange({ target: { value: "" } })
                    }
                    size="small"
                    sx={{
                      backgroundColor: (theme) =>
                        `rgba(${parseInt(
                          theme.palette.secondary.main.slice(1, 3),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(3, 5),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(5, 7),
                          16
                        )}, 0.1)`,
                      "& .MuiChip-deleteIcon": {
                        color: "secondary.main",
                        "&:hover": {
                          color: "secondary.dark",
                        },
                      },
                    }}
                  />
                )}

              {selectedSubcategory &&
                categories.find((c) => c.id === selectedSubcategory) && (
                  <Chip
                    label={`${tProducts("catalog.filters.subcategories")}: ${
                      categories.find((c) => c.id === selectedSubcategory).name
                    }`}
                    onDelete={() =>
                      handleSubcategoryChange({ target: { value: "" } })
                    }
                    size="small"
                    sx={{
                      backgroundColor: (theme) =>
                        `rgba(${parseInt(
                          theme.palette.secondary.main.slice(1, 3),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(3, 5),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(5, 7),
                          16
                        )}, 0.1)`,
                      "& .MuiChip-deleteIcon": {
                        color: "secondary.main",
                        "&:hover": {
                          color: "secondary.dark",
                        },
                      },
                    }}
                  />
                )}

              {/* Price Range Filter */}
              {userSetPriceRange &&
                minMaxPrice &&
                minMaxPrice.length === 2 &&
                (priceRange[0] > minMaxPrice[0] ||
                  priceRange[1] < minMaxPrice[1]) && (
                  <Chip
                    label={`${tProducts("catalog.filters.priceRange")}: $${
                      priceRange[0]
                    } - $${priceRange[1]}`}
                    onDelete={() => {
                      setPriceRange(minMaxPrice);
                      setUserSetPriceRange(false);
                    }}
                    size="small"
                    sx={{
                      backgroundColor: (theme) =>
                        `rgba(${parseInt(
                          theme.palette.secondary.main.slice(1, 3),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(3, 5),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(5, 7),
                          16
                        )}, 0.1)`,
                      "& .MuiChip-deleteIcon": {
                        color: "secondary.main",
                        "&:hover": {
                          color: "secondary.dark",
                        },
                      },
                    }}
                  />
                )}

              {/* Weight Range Filter */}
              {userSetWeightRange &&
                minMaxWeight &&
                minMaxWeight.length === 2 &&
                (weightRange[0] > minMaxWeight[0] ||
                  weightRange[1] < minMaxWeight[1]) && (
                  <Chip
                    label={`${tProducts("catalog.filters.weightRange")}: ${
                      weightRange[0]
                    }ct - ${weightRange[1]}ct`}
                    onDelete={() => {
                      setWeightRange(minMaxWeight);
                      setUserSetWeightRange(false);
                    }}
                    size="small"
                    sx={{
                      backgroundColor: (theme) =>
                        `rgba(${parseInt(
                          theme.palette.secondary.main.slice(1, 3),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(3, 5),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(5, 7),
                          16
                        )}, 0.1)`,
                      "& .MuiChip-deleteIcon": {
                        color: "secondary.main",
                        "&:hover": {
                          color: "secondary.dark",
                        },
                      },
                    }}
                  />
                )}

              {Object.entries(attributeFilters).map(([key, value]) => {
                if (!value) return null;
                const attribute = availableAttributes.find((a) => a.id === key);
                if (!attribute) return null;

                return (
                  <Chip
                    key={key}
                    label={`${attribute.name}: ${value}`}
                    onDelete={() => handleAttributeFilterChange(key, "")}
                    size="small"
                    sx={{
                      backgroundColor: (theme) =>
                        `rgba(${parseInt(
                          theme.palette.secondary.main.slice(1, 3),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(3, 5),
                          16
                        )}, ${parseInt(
                          theme.palette.secondary.main.slice(5, 7),
                          16
                        )}, 0.1)`,
                      "& .MuiChip-deleteIcon": {
                        color: "secondary.main",
                        "&:hover": {
                          color: "secondary.dark",
                        },
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Product Grid */}
          <ProductGrid products={products} loading={loading} />

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                mb: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="secondary"
                size={isMobile ? "small" : "medium"}
                siblingCount={isMobile ? 0 : 3}
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "text.primary",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "action.selected",
                    opacity: 0.1,
                    color: "secondary.main",
                    "&:hover": {
                      backgroundColor: "action.selected",
                      opacity: 0.2,
                    },
                  },
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        open={filterDrawerOpen}
        onClose={() => toggleFilterDrawer(false)}
        filterProps={filterProps}
      />
    </Container>
  );
};

export default ProductCatalogPage;
